'use client';

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, MoreHorizontal, Star, Trash } from 'lucide-react';
import { AdvancedImage } from '@cloudinary/react';
import { LiveMessage } from '../../data/liveMessageData/liveMessage';
import { useTranslations } from 'next-intl';

interface ImagePreviewPopupProps {
  isOpen: boolean;
  onClose: () => void;
  message: LiveMessage | null;
  onDeleteMessage?: (messageId: string) => Promise<void>;
  onSetDefaultImage?: (messageId: string) => Promise<void>;
}

export default function ImagePreviewPopup({ 
  isOpen, 
  onClose, 
  message,
  onDeleteMessage,
  onSetDefaultImage
}: ImagePreviewPopupProps) {
  const t = useTranslations('chat');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const advancedImageRef = useRef<any>(null);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Handle escape key to close
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Reset text expansion when popup opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsTextExpanded(false);
      setIsMenuOpen(false);
    }
  }, [isOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMenuOpen && wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleWrapperClick = (event: React.MouseEvent) => {
    const target = event.target as Element;
    
    // Don't close if clicking on the text overlay area
    const textOverlay = target.closest('[data-text-overlay]');
    if (textOverlay) {
      return;
    }
    
    // Close the preview popup
    onClose();
  };

  const toggleTextExpansion = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsTextExpanded(!isTextExpanded);
  };

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleDeleteMessage = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteMessage && message) {
      try {
        await onDeleteMessage(message.id);
        setIsMenuOpen(false);
        onClose(); // Close the preview after deletion
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  const handleSetDefaultImage = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSetDefaultImage && message) {
      try {
        await onSetDefaultImage(message.id);
        setIsMenuOpen(false);
      } catch (error) {
        console.error('Error setting image as default:', error);
      }
    }
  };


  if (!isOpen || !message || !message.imageWithStatus.imageData) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-50 p-4">
      <div 
        ref={wrapperRef}
        className="relative w-full h-full flex items-center justify-center"
        onClick={handleWrapperClick}
      >
        {/* Menu button - positioned at top right */}
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={toggleMenu}
            className="text-white hover:text-gray-300 transition-colors duration-200 bg-black/60 backdrop-blur-sm rounded-full p-2 shadow-lg"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="absolute top-10 right-0 bg-[#1a1a1a] rounded-xl shadow-2xl z-[999999] w-auto min-w-[160px] whitespace-nowrap">
              <button
                onClick={handleSetDefaultImage}
                className={`w-full flex items-center justify-start px-4 py-2 text-white hover:bg-[#ffffff1a] transition-colors text-sm ${
                  message.id.startsWith('default') ? 'rounded-xl' : 'rounded-t-xl'
                }`}
              >
                <Star className="w-4 h-4 mr-2" />
                {t('setAsBackground')}
              </button>
              {!message.id.startsWith('default') && (
                <button
                  onClick={handleDeleteMessage}
                  className="w-full flex items-center justify-start px-4 py-2 text-red-400 hover:bg-[#ffffff1a] hover:text-red-300 transition-colors rounded-b-xl text-sm"
                >
                  <Trash className="w-4 h-4 mr-2" />
                  {t('delete')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Image container - larger and fits screen */}
        <div className="relative">
          {/* Show Cloudinary image */}
          <AdvancedImage
            ref={advancedImageRef}
            cldImg={message.imageWithStatus.imageData}
            alt={message.content || "Image loading failed"}
            className="max-w-full max-h-[90vh] object-contain"
            loading="eager"
          />
        </div>

        {/* Prompt text overlay - anchored to popup bottom */}
        {/* {message.content && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm text-white p-4 rounded-t-lg max-h-[33.33vh] overflow-y-auto" data-text-overlay>
            <div className="flex items-start justify-between gap-3">
              <div className={`text-sm leading-relaxed flex-1 ${
                !isTextExpanded ? 'line-clamp-3' : ''
              }`}>
                {message.content}
              </div>
              <button
                onClick={toggleTextExpansion}
                className="flex-shrink-0 text-white hover:text-gray-300 transition-colors duration-200 p-1"
                title={isTextExpanded ? "Show less" : "Show more"}
              >
                {isTextExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
}
